import * as React from "react";
import { useIntl } from "react-intl";
import { connect } from "react-redux";
import { Routine } from "redux-saga-routines";
import {Action, ActionFunctionAny} from "redux-actions";
import Chart from "react-apexcharts";
import {SettingEnum, SettingEnum as ComponentSettingEnum} from "../../settingEnum";
import { AppState } from "@Safemate/Store/types";
import { getStepData, toggleDevicePedometer } from "@Safemate/Settings/Store/routines";
import { DevicePedometer } from "@Safemate/Model/DevicePedometer";
import moment from "moment";
import styled from "styled-components";
import Toggle from "@Elements/Switch/Toggle";
import { SettingsLayout, Section } from "../../Components/settingsLayout";

const ChartWrapper = styled.div`
  .apexcharts-tooltip {
    background: ${props => props.theme.colors.backgroundPrimary} !important;
    color: ${props => props.theme.colors.textPrimary};
  }
  .apexcharts-tooltip-title{
    border-bottom: none !important;
    background: ${props => props.theme.colors.backgroundPrimary} !important;
    color: ${props => props.theme.colors.textPrimary};
  }
  text{
    fill: ${props => props.theme.colors.textPrimary};
  }
`

const mapStateToProps = ({ settings: {initialization: { dehaId, deviId }}}: AppState) => {
  return{
    dehaId,
    deviId
  }
}

const mapDispatchToProps = {
  getStepData
}

interface StepDataProps{
  dehaId: number;
  deviId: number;
  getStepData: Routine<ActionFunctionAny<Action<any>>>;
  components: Map<SettingEnum, JSX.Element[]>;
}

const StepData = connect(mapStateToProps, mapDispatchToProps)(({ dehaId, deviId, getStepData, components }: StepDataProps) => {
 
  const { formatMessage } = useIntl();

  React.useEffect(() => {
    getStepData(deviId);
  }, [])
  
  return(
    <SettingsLayout>
      <h2>{formatMessage({id: "stepData", defaultMessage: "Skritteller"})}</h2>
      <Section>
          {components.has(ComponentSettingEnum.DEVICE_PEDOMETER) 
            && components.get(ComponentSettingEnum.DEVICE_PEDOMETER)?.map((comp, i) => 
                <React.Fragment key={i}>{comp}</React.Fragment>)}
        </Section>
    </SettingsLayout>
  )
})

export default StepData;

const mapStateToStepInformationProps = ({ settings: { general: { stepData }}}: AppState) => {
  return {
    stepData
  }
}

interface StepInformationProps{
  stepData: DevicePedometer[]
}

interface MappedDay{
  date: string;
  steps: number;
}

export const StepInformation = connect(mapStateToStepInformationProps)(({ stepData }: StepInformationProps) => {

  const [ selectedDate, setSelectedDate ] = React.useState();
  const [ range, setRange ] = React.useState(14);
  const { formatMessage } = useIntl();

  const [mappedDaily, hasData] = React.useMemo(() => {

    const today = moment();
    const days: {[index: string]: number} = {};
    let data = false;

    days[today.format("DD.MM.YYYY")] = 0;
    for(let i = 0; i <= range; i++){
      const currentDay = today.subtract(1, "days");
      days[currentDay.format("DD.MM.YYYY")] = 0;
    }

    stepData.forEach(step => {

      const date = moment(step.date);
      const currentDate = date.format("DD.MM.YYYY");

      let stepsDay = days[currentDate] || 0;
      stepsDay += step.steps;
      days[currentDate] = stepsDay;

      if(stepsDay > 0){
        data = true;
      }

    })

    const mapped: MappedDay[] = [];

    Object.keys(days).forEach(day => {
      mapped.push({
        date: day,
        steps: days[day]
      })
    })

    return [mapped, data];

  }, [stepData, range])

  if(!hasData) return <div>{formatMessage({id: "noData", defaultMessage: "Ingen data"})}</div>;


  const options = {
    chart: {
      id: "basic-bar"
    },
    xaxis: {
      categories: mappedDaily.map(day => day.date).reverse()
    }
  }
  const series = [
    {
      name: formatMessage({id: "steps", defaultMessage: "Skritt"}),
      data: mappedDaily.map(day => day.steps).reverse()
    }
  ]


  return (
    <div className="app">
      <div className="row">
        <ChartWrapper className="mixed-chart">
          <Chart
            options={options}
            series={series}
            type="bar"
          />
        </ChartWrapper>
      </div>
    </div>
  )
})

const mapStateToPedometerToggleProps = ({ settings: { initialization: { deviId }, general: { devicePedometer } } }: AppState) => {
  return {
    deviId,
    devicePedometer
  }
}

const mapDispatchToPedometerToggleProps = {
  toggleDevicePedometer
}

interface PedometerToggleProps{
  toggleDevicePedometer: Routine<ActionFunctionAny<Action<any>>>;
  deviId: number;
  devicePedometer: boolean;
}

export const PedometerToggle = connect(mapStateToPedometerToggleProps, mapDispatchToPedometerToggleProps)(({toggleDevicePedometer, deviId, devicePedometer}: PedometerToggleProps) => {

  return(
    <div>
      <Toggle
        onChange={(value: boolean) => {
          toggleDevicePedometer({deviId, value});
        }}
        checked={devicePedometer}
      />
    </div>
  )
})
